import {mergeMap} from 'rxjs/operators';

import {FrontWindow, globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskMetadata} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {fromAnimationFrameOrHiddenCallback} from '../../../srcCommon/rx/fromAnimationFrame';
import {CancelToken, toCancelablePromise} from '../asyncCancelHelpers';

/**
 * Await-able version of requestAnimationFrame that propagates the return value.
 *
 * Warning: animation frames don't fire while the window is hidden or obscured. Make sure you target the right window!
 */
export async function inAnimationFrameAsync<R>(
  window: FrontWindow,
  callback: (time: number) => R | Promise<R>,
  metadata: TaskMetadata,
  cancelToken?: CancelToken,
): Promise<R> {
  return new Promise<R>((resolve, reject) => {
    const handle = window.requestInstrumentedAnimationFrame((time: number) => {
      try {
        resolve(callback(time));
      } catch (error) {
        reject(error);
      }
    }, metadata);

    if (!cancelToken) {
      return;
    }

    cancelToken.promise.then(() => {
      window.cancelAnimationFrame(handle);
    });
  });
}

export async function inAnimationFrameOrHiddenCallbackAsync<R>(
  callback: (time: number | undefined) => R | Promise<R>,
  metadata: TaskMetadata,
  cancelToken?: CancelToken,
  window = globalWindow,
): Promise<R> {
  return toCancelablePromise(
    fromAnimationFrameOrHiddenCallback(metadata, window).pipe(mergeMap(async (time) => callback(time))),
    cancelToken,
  );
}
